import { reactive } from 'vue';
import type { ModalRequest, DialogOptions, Toast, LightboxRequest } from './types';
import type { Component } from 'vue';

export class OverlayManager {
  modals = reactive<ModalRequest<unknown, unknown>[]>([]);
  dialog = reactive<{ options?: DialogOptions; resolve?: (result: boolean) => void }>({});
  toasts = reactive<Toast[]>([]);
  toastCounter = 0;

  lightbox = reactive<{ request?: LightboxRequest; currentIndex: number }>({ currentIndex: 0 });

  // Modal
  openModal<Props = unknown, Result = unknown>(
    component: Component,
    props?: Props
  ): Promise<Result> {
    return new Promise<Result>((resolve) => {
      this.modals.push({
        component,
        props,
        resolve: resolve as (result: unknown) => void
      });
    });
  }

  closeModal(result: unknown | null) {
    const modal = this.modals.pop();
    modal?.resolve(result);
  }

  // Dialog (Confirm)
  confirm(options: DialogOptions): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.dialog.options = options;
      this.dialog.resolve = resolve;
    });
  }

  resolveDialog(result: boolean) {
    this.dialog.resolve?.(result);
    this.dialog.options = undefined;
  }

  // Toast
  showToast(options: Omit<Toast, 'id'>) {
    const id = ++this.toastCounter;
    this.toasts.unshift({ duration: 4000, severity: 'info', ...options, id });
  }

  removeToast(id: number) {
    const index = this.toasts.findIndex(t => t.id === id);
    if (index !== -1) {
      this.toasts.splice(index, 1);
    }
  }

  // Lightbox
  openLightbox(images: string[], startIndex = 0): Promise<void> {
    return new Promise((resolve) => {
      this.lightbox.request = { images, startIndex, resolve };
      this.lightbox.currentIndex = startIndex;
    });
  }

  closeLightbox() {
    this.lightbox.request?.resolve();
    this.lightbox.request = undefined;
    this.lightbox.currentIndex = 0;
  }

  nextLightboxImage() {
    if (!this.lightbox.request) return;
    this.lightbox.currentIndex = (this.lightbox.currentIndex + 1) % this.lightbox.request.images.length;
  }

  prevLightboxImage() {
    if (!this.lightbox.request) return;
    this.lightbox.currentIndex = (this.lightbox.currentIndex - 1 + this.lightbox.request.images.length) % this.lightbox.request.images.length;
  }
}
